/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { getCurrentUser, clearUserCache } from "services/authService";

/**
 * UserContext - Global user state management
 * 
 * Prevents duplicate API calls by caching user data globally.
 * All components can access user data without making separate API calls.
 * 
 * Multi-worker compatible: Uses cookie-based authentication (no memory state issues)
 */
const UserContext = createContext(null);

/**
 * UserProvider - Provides user context to all child components
 * 
 * Features:
 * - Single API call on mount (cached for 2 minutes)
 * - Automatic refresh on route changes
 * - Loading and error states
 * - Force refresh capability
 */
export function UserProvider({ children }) {
  // Sayfa yenilendiğinde cache'den user bilgisini kontrol et (production'da cookie okunamama durumunda)
  const [user, setUser] = useState(() => {
    // Sayfa yenilendiğinde cache'den user bilgisini kontrol et
    // Bu sayede production'da cookie okunamama durumunda user state'i korunur
    try {
      // Kullanıcı profili cache'i (token değil — token yalnızca HttpOnly cookie'de)
      const cachedUser = sessionStorage.getItem('user_cache');
      if (cachedUser) {
        const parsed = JSON.parse(cachedUser);
        // Cache'in geçerliliğini kontrol et (2 dakika)
        const cacheTimestamp = sessionStorage.getItem('user_cache_timestamp');
        if (cacheTimestamp && (Date.now() - parseInt(cacheTimestamp)) < 120000) {
          return parsed;
        }
      }
    } catch (e) {
      // Cache okuma hatası - sorun değil, API'den çekeceğiz
    }
    return null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();

  // Fetch user data (with caching handled by authService)
  const fetchUser = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      const userData = await getCurrentUser(forceRefresh);
      setUser(userData);
      // Cache'i sessionStorage'a kaydet (sayfa yenileme için)
      try {
        sessionStorage.setItem('user_cache', JSON.stringify(userData));
        sessionStorage.setItem('user_cache_timestamp', Date.now().toString());
      } catch (e) {
        // sessionStorage yazma hatası - sorun değil
      }
    } catch (err) {
      // Sadece 401 hatası durumunda user'ı null yap
      // Diğer hatalarda (network error, timeout, vs.) user state'i koru
      // Çünkü 401 durumunda authService zaten login'e yönlendiriyor
      const isUnauthorized = err.message?.includes("Oturum süresi doldu") || 
                            err.message?.includes("Kimlik doğrulama başarısız") ||
                            err.status === 401;
      
      if (isUnauthorized) {
        setUser(null);
        // Cache'i temizle
        try {
          sessionStorage.removeItem('user_cache');
          sessionStorage.removeItem('user_cache_timestamp');
        } catch (e) {
          // sessionStorage temizleme hatası - sorun değil
        }
        // 401 durumunda authService zaten login'e yönlendiriyor, burada bir şey yapmaya gerek yok
      } else {
        // Network error, timeout vs. durumlarında sadece error set et, user'ı koru
        setError(err.message || "Kullanıcı bilgisi alınamadı");
        // User state'ini koru - belki geçici bir network sorunu
        // Cache'deki user bilgisini kullanmaya devam et
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch on mount ve route değişikliğinde - Login sayfasındayken skip et (sonsuz döngü önleme)
  useEffect(() => {
    // Login sayfasındayken /auth/me çağrısı yapma (cookie yoksa sonsuz redirect döngüsü oluşur)
    const isLoginPage = window.location.hash === '#/login' || window.location.pathname === '/login' || location.pathname === '/login';
    if (!isLoginPage) {
      fetchUser();
    } else {
      // Login sayfasındayken loading'i false yap
      setLoading(false);
    }
  }, [fetchUser, location.pathname]);

  // Clear user data (for logout)
  const clearUser = useCallback(() => {
    setUser(null);
    setError(null);
    clearUserCache();
    // SessionStorage cache'ini de temizle
    try {
      sessionStorage.removeItem('user_cache');
      sessionStorage.removeItem('user_cache_timestamp');
    } catch (e) {
      // sessionStorage temizleme hatası - sorun değil
    }
  }, []);

  // Refresh user data (force refresh)
  const refreshUser = useCallback(() => {
    return fetchUser(true);
  }, [fetchUser]);

  const value = {
    user,
    loading,
    error,
    refreshUser,
    clearUser,
    // Convenience getters
    userRole: user?.role || null,
    userBayiTipi: user?.bayi_tipi || null,
    userId: user?.id || null,
    username: user?.username || null,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

/**
 * useUser - Hook to access user context
 * 
 * @returns {object} { user, loading, error, refreshUser, clearUser, userRole, userBayiTipi, userId, username }
 */
export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

/**
 * useUserRole - Convenience hook for backward compatibility
 * 
 * @returns {string|null} userRole - Current user's role or null
 */
export function useUserRole() {
  const { userRole } = useUser();
  return userRole;
}

export default UserContext;
